/**
 * Phone navigation: Hoje · Plano · [Treinar] · Progresso · Perfil (`D218`).
 * A2 `nav.tsx:57-175`, with the trainer moved off the bar to its floating
 * bubble (`coach-bubble.tsx`) and the person's tab in its place.
 *
 * - "Perfil" carries the dot the header avatar used to: something the plan
 *   is built from is still unanswered — or a release in "Novidades" is not
 *   yet seen on this device (`D263`).
 * - The central 64 px button starts a workout (`screen=workout&start=1`).
 *   With a session running it shows the live clock (or the rest countdown)
 *   and a done/total ring, and only brings the workout back.
 * - The bar is fixed to the viewport (the app frame has no transform), paints
 *   down through the gesture area and pads itself clear of it (`pb-safe`).
 *   It adds no padding to the screen: a screen's `Scroll` already keeps its
 *   content clear of the bar (`pb-36`), as in A2.
 */
import {
  "shell.live_rest_label" as liveRestLabel,
  "shell.live_running_label" as liveRunningLabel,
  "shell.nav_label" as navLabel,
  "shell.profile_label_pct" as profileLabelPct,
  "community.tab_profile_news" as profileNewsLabel,
  "shell.tab_plan_draft" as tabPlanDraft,
  "shell.train" as trainLabel,
  "shell.train_now" as trainNow,
} from "@repo/i18n";
import { useCallback, useEffect, useRef } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../../ui/controls";
import { useToastClearance } from "../../ui/overlay";
import { useUnseenNews } from "../community/news-seen";
import { useNavBadges } from "./badges";
import { type LiveSessionView, useLiveSession } from "./live-session";
import { TABS, type Tab, useNavigation } from "./navigation";
import { activeTab, setPendingTab, usePendingTab } from "./pending-tab";
import { PlayGlyph } from "./play-glyph";
import { useProfileCompleteness } from "./use-profile-completeness";

function TabButton({ tab }: { tab: Tab }) {
  const { screen, go } = useNavigation();
  const badges = useNavBadges();
  const pressed = usePendingTab();
  const complete = useProfileCompleteness();
  const unseenNews = useUnseenNews().length > 0;
  // The pressed tab is drawn active from the touch, not from the router's
  // commit a beat later (`pending-tab.ts`, `D193`).
  const active = tab.id !== "coach" && activeTab(screen, pressed) === tab.id;
  const draftBadge = tab.id === "plan" && badges.planDraft;
  // Until the percentage is known the dot says nothing, rather than guess.
  const incomplete = complete !== null && complete.pct < 100;
  // A release not yet seen in "Novidades" lights the same dot (`D263`).
  const profileBadge = tab.id === "profile" && (incomplete || unseenNews);
  const label = tab.label();
  let ariaLabel: string | undefined;
  if (draftBadge) {
    ariaLabel = tabPlanDraft();
  } else if (profileBadge && incomplete) {
    ariaLabel = profileLabelPct({ pct: complete.pct });
  } else if (profileBadge) {
    ariaLabel = profileNewsLabel();
  }
  const onClick = useCallback(() => go(tab.id), [go, tab.id]);
  const onPointerDown = useCallback(() => {
    if (tab.id !== "coach") {
      setPendingTab(tab.id);
    }
  }, [tab.id]);
  const onPointerCancel = useCallback(() => setPendingTab(null), []);
  return (
    <button
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel}
      className="group flex h-full min-w-12 flex-col items-center justify-center gap-1"
      onClick={onClick}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      type="button"
    >
      <span
        className={cx(
          "relative grid h-8 w-14 place-items-center rounded-full transition-[background-color,color,scale] duration-150 group-active:scale-[0.92]",
          active
            ? "bg-accent-soft text-accent-ink"
            : "text-ink-3 group-active:bg-surface-2"
        )}
      >
        <Icon bold={active} icon={tab.icon} size={24} />
        {draftBadge || profileBadge ? (
          <span
            aria-hidden="true"
            className="absolute top-0.5 right-3 size-2.5 rounded-full bg-accent ring-2 ring-surface"
            data-nav-badge
          />
        ) : null}
      </span>
      <span
        className={cx(
          "text-xs leading-none",
          active ? "font-semibold text-ink" : "font-medium text-ink-3"
        )}
      >
        {label}
      </span>
    </button>
  );
}

// The halo sits 4 px outside the 64 px button, inside its 6 px surface ring.
const RING_R = 36;
const RING_C = 2 * Math.PI * RING_R;
const RING_BOX = RING_R * 2 + 4;

function liveLabel(live: LiveSessionView): string {
  const inputs = { clock: live.clock, done: live.done, total: live.total };
  return live.resting ? liveRestLabel(inputs) : liveRunningLabel(inputs);
}

function TrainButton() {
  const { train } = useNavigation();
  const live = useLiveSession();
  const isLive = live !== null;
  const onClick = useCallback(() => train(isLive), [train, isLive]);
  // The BUTTON, not the `<nav>`: it is absolutely positioned 28 px above the
  // bar, so the bar's own rect does not contain it and a toast cleared of the
  // bar still lands on the play button (T48).
  const button = useRef<HTMLButtonElement>(null);
  useToastClearance(button);
  return (
    <div className="relative flex flex-col items-center justify-end pb-3.5">
      <button
        aria-label={live ? liveLabel(live) : trainNow()}
        className="a-press a-primary absolute -top-7 left-1/2 grid size-16 -translate-x-1/2 place-items-center rounded-full ring-6 ring-surface"
        data-train-button
        onClick={onClick}
        ref={button}
        type="button"
      >
        <PlayGlyph className="ml-1 size-7" />
        {live ? (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90"
            height={RING_BOX}
            width={RING_BOX}
          >
            <circle
              className="stroke-accent-soft"
              cx={RING_R + 2}
              cy={RING_R + 2}
              fill="none"
              r={RING_R}
              strokeWidth={3}
            />
            <circle
              className="stroke-accent"
              cx={RING_R + 2}
              cy={RING_R + 2}
              fill="none"
              r={RING_R}
              strokeDasharray={`${(live.done / live.total) * RING_C} ${RING_C}`}
              strokeLinecap="round"
              strokeWidth={3}
            />
          </svg>
        ) : null}
      </button>
      {live ? (
        <span
          aria-hidden="true"
          className={cx(
            "flex items-center gap-1 font-semibold text-xs tabular-nums leading-none",
            live.resting ? "text-accent-ink" : "text-ink"
          )}
        >
          <span className="size-1.5 rounded-full bg-accent p-live" />
          {live.clock}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="font-semibold text-accent-ink text-xs leading-none"
        >
          {trainLabel()}
        </span>
      )}
    </div>
  );
}

export function TabBar() {
  const [today, plan, progress, profile] = TABS;
  // The URL caught up with the press: the pending tab has landed.
  const { screen } = useNavigation();
  const pressed = usePendingTab();
  useEffect(() => {
    if (pressed === screen) {
      setPendingTab(null);
    }
  }, [pressed, screen]);
  return (
    <nav
      aria-label={navLabel()}
      className="fixed inset-x-0 bottom-0 z-30 lg:hidden"
      data-tab-bar
    >
      <div className="border-line border-t bg-surface pb-safe">
        <div className="mx-auto grid h-18 max-w-140 grid-cols-5">
          <TabButton tab={today} />
          <TabButton tab={plan} />
          <TrainButton />
          <TabButton tab={progress} />
          <TabButton tab={profile} />
        </div>
      </div>
    </nav>
  );
}
